/**
 * @typedef {Object} ConstructorProduct
 * @property {string} handle - Product handle/slug
 * @property {Object} constructorData - Raw Constructor.io data
 * @property {Array} variations - Product variations
 * @property {boolean} isSlotted - Whether product is slotted
 */

/**
 * Error boundary for Constructor.io operations
 */
class ConstructorErrorBoundary {
  constructor() {
    this.errors = [];
    this.maxErrors = 10;
  }

  /**
   * Wrap async operations with error handling
   */
  async wrap(fn) {
    try {
      return await fn();
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Handle and log errors with context
   */
  handleError(error, context = "unknown") {
    const errorInfo = {
      message: error.message,
      stack: error.stack,
      context,
      timestamp: Date.now(),
      url: window.location.href,
    };

    this.errors.push(errorInfo);

    // Keep only last N errors
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }

    // Log to console in development
    if (window.Shopify && window.Shopify.designMode) {
      console.error("[Constructor.io Error]", errorInfo);
    }

    // Dispatch error event for monitoring
    document.dispatchEvent(
      new CustomEvent("constructor:error", {
        detail: errorInfo,
      })
    );
  }

  /**
   * Get recent errors for debugging
   */
  getErrors() {
    return [...this.errors];
  }
}

/**
 * Constructor.io Browse Integration
 * @class
 */
class ConstructorBrowse {
  constructor() {
    // Singleton pattern
    if (ConstructorBrowse.instance) {
      return ConstructorBrowse.instance;
    }

    this.client = null;
    this.isInitialized = false;
    this.requestSequence = 0;
    this.currentCollectionHandle = "";
    this.currentFilters = {};
    this.currentFacets = [];
    this.selectorCache = new Map();
    this.initializationPromise = null;
    this.errorBoundary = new ConstructorErrorBoundary();

    ConstructorBrowse.instance = this;
    this.initialize();
  }

  /**
   * Get or create singleton instance
   */
  static getInstance() {
    if (!ConstructorBrowse.instance) {
      ConstructorBrowse.instance = new ConstructorBrowse();
    }
    return ConstructorBrowse.instance;
  }

  sanitizeHTML(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  buildSizedImageUrl(src, width, height) {
    if (!src) return "";

    if (src.includes("width=") || src.includes("height=") || src.includes("_x")) {
      return src;
    }

    const separator = src.includes("?") ? "&" : "?";
    return `${src}${separator}width=${width}&height=${height}&crop=center`;
  }

  buildResponsiveAttrs(rawSrc, width, height) {
    if (!rawSrc) return { src: "", srcset: "", sizes: "" };

    const src = this.buildSizedImageUrl(rawSrc, width, height);
    const src2x = this.buildSizedImageUrl(rawSrc, width * 2, height * 2);

    const widths = [300, 600, 900];
    const srcsetByWidth = widths
      .map((w) => `${this.buildSizedImageUrl(rawSrc, w, Math.round((w * height) / width))} ${w}w`)
      .join(", ");

    const sizes = "(min-width: 768px) 300px, 50vw";

    return {
      src,
      srcset: `${srcsetByWidth}, ${src} 1x, ${src2x} 2x`,
      sizes,
    };
  }

  /**
   * Single initialization with proper error handling and Promise-based approach
   */
  async initialize() {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.errorBoundary.wrap(async () => {
      try {
        if (window.constructorManager) {
          this.client = window.constructorManager.getClient();
          if (!this.client) {
            await this.waitForConstructor();
            this.client = window.constructorManager.getClient();
          }
        } else {
          await this.waitForConstructor();
          this.client = window.constructorio;
        }

        if (!this.client) {
          throw new Error("Constructor.io client not available after initialization");
        }

        this.setupEventListeners();
        this.bindEvents();

        this.isInitialized = true;

        document.dispatchEvent(
          new CustomEvent("constructor:browse:ready", {
            detail: { instance: this },
          })
        );

        return true;
      } catch (error) {
        this.errorBoundary.handleError(error, "initialization");
        return false;
      }
    });

    return this.initializationPromise;
  }

  /**
   * Wait for Constructor.io to be available with timeout
   */
  waitForConstructor(timeout = 10000) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const check = () => {
        if (window.constructorio && window.cnstrc && window.cnstrc.indexKey) {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          reject(new Error("Constructor.io initialization timeout"));
        } else {
          setTimeout(check, 50);
        }
      };

      check();
    });
  }

  setupEventListeners() {}

  /**
   * Query selector with cache
   * @param {string} selector - Seletor CSS
   * @returns {Element|null} - Elemento encontrado
   */
  querySelector(selector) {
    if (!this.selectorCache.has(selector)) {
      this.selectorCache.set(selector, document.querySelector(selector));
    }
    return this.selectorCache.get(selector);
  }

  /**
   * Clear selector cache
   */
  clearSelectorCache() {
    this.selectorCache.clear();
  }

  /**
   * Fetch products from a collection
   * @param {string} collectionHandle - Collection handle
   * @param {Object} options - Search options
   * @returns {Promise<boolean>} - Success of operation
   */
  async handleBrowse(collectionHandle, options = {}) {
    if (!this.isInitialized || !this.client || !window.constructorio) {
      console.warn("Constructor Browse not properly initialized");
      return false;
    }

    this.requestSequence++;
    const mySequence = this.requestSequence;

    this.currentCollectionHandle = collectionHandle;

    if (options.filters !== undefined) {
      this.currentFilters = options.filters;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = options.page || parseInt(urlParams.get("page")) || 1;
    const resultsPerPage = options.resultsPerPage || parseInt(urlParams.get("resultsPerPage")) || 40;

    let sortBy = options.sortBy || urlParams.get("sortBy");
    let sortOrder = options.sortOrder || urlParams.get("sortOrder");

    const legacySort = urlParams.get("sort");
    if (!sortBy && legacySort && legacySort.includes(":")) {
      const [sb, so] = legacySort.split(":");
      sortBy = sb || "relevance";
      sortOrder = so || "descending";
    }
    sortBy = sortBy || "relevance";
    sortOrder = sortOrder || "descending";

    if (options.filters === undefined) {
      const urlFilters = urlParams.get("filters");
      let parsedFilters = {};
      if (urlFilters) {
        try {
          parsedFilters = JSON.parse(decodeURIComponent(urlFilters));
          this.currentFilters = { ...parsedFilters, ...this.currentFilters };
        } catch (e) {
          // Invalid filters in URL, ignore.
        }
      }
    }

    this.currentPage = currentPage;
    try {
      const container = this.getResultsContainer();
      if (container) {
        container.classList.add("constructor-loading-subtle");
      }

      // Clean options to remove undefined/null values that could cause double ampersands
      const cleanOptions = {};
      Object.keys(options).forEach((key) => {
        const value = options[key];
        if (value !== undefined && value !== null && value !== "") {
          cleanOptions[key] = value;
        }
      });

      // Build browseParams with proper validation to prevent empty parameters
      const browseParams = {};

      // Add required parameters with validation
      if (resultsPerPage !== undefined && resultsPerPage !== null) {
        browseParams.resultsPerPage = resultsPerPage;
      }

      browseParams.section = "Products";

      // Only add filters if they have actual values
      if (this.currentFilters && Object.keys(this.currentFilters).length > 0) {
        browseParams.filters = this.currentFilters;
      }

      // Only add sort parameters if they have values
      if (sortBy !== undefined && sortBy !== null && sortBy !== "") {
        browseParams.sortBy = sortBy;
      }

      if (sortOrder !== undefined && sortOrder !== null && sortOrder !== "") {
        browseParams.sortOrder = sortOrder;
      }

      // Only add page if it's a valid number
      if (currentPage !== undefined && currentPage !== null && currentPage > 0) {
        browseParams.page = currentPage;
      }

      // Always include fmt_options
      browseParams.fmt_options = {
        show_hidden_facets: false,
        show_hidden_fields: false,
        fields: [
          "data.handle",
          "data.title",
          "data.price",
          "data.compareAtPrice",
          "data.image_url",
          "data.inventory_quantity",
          "data.group_ids",
          "data.preorder_tag",
          "data.waterproof_tag",
          "data.bestseller_tag",
          "data.new_tag",
          "data.mediaImages",
          "data.url",
          "data.tags",
          "data.vendor",
          "data.product_type",
          "data.options",
          "data.fake_discount_percentage",
          "data.fake_discount_message",
          "data.created_at",
          "data.updated_at",
          "data.published_at",
          "data.created_date",
          "variations",
        ],
      };

      // Add cleaned options
      Object.assign(browseParams, cleanOptions);

      const response = await this.client.browse.getBrowseResults("group_id", collectionHandle, browseParams, {
        timeout: 10000,
      });

      if (mySequence !== this.requestSequence) {
        return false;
      }

      const results = response.response?.results || response.results || [];

      this.currentFacets = response.response?.facets || [];
      this.currentSortOptions = response.response?.sort_options || [];

      // Update sort dropdown with Constructor.io options
      this.updateSortDropdown();

      if (options.appendToExisting && results.length === 0) {
        this.hideLoadMore();
        return true;
      }

      const constructorProducts = results
        .map((item, index) => {
          const handle =
            item.data?.url?.split("/products/")[1]?.split("?")[0] ||
            item.data?.handle ||
            item.data?.slug ||
            item.data?.shopify_handle ||
            item.value?.toLowerCase().replace(/\s+/g, "-") ||
            item.handle ||
            (item.data?.id && typeof item.data.id === "string" && /[a-zA-Z-]/.test(item.data.id) ? item.data.id : null) ||
            (item.data?.item_id && typeof item.data.item_id === "string" && /[a-zA-Z-]/.test(item.data.item_id)
              ? item.data.item_id
              : null);

          return {
            handle: handle,
            constructorData: item,
            variations: item.variations || [],
            value: item.value,
            matchedTerms: item.matched_terms || [],
            isSlotted: item.is_slotted || false,
          };
        })
        .filter((item) => item.handle);

      const productHandles = constructorProducts.map((item) => item.handle);

      this.totalResultsFromAPI = response.response?.total_num_results || results.length;

      await this.renderProducts(productHandles, {
        collectionHandle: collectionHandle,
        resultId: response.result_id,
        totalResults: this.totalResultsFromAPI,
        constructorProducts: constructorProducts,
        currentPage: currentPage,
        resultsPerPage: resultsPerPage,
        ...options,
      });

      this.updateURL(collectionHandle, {
        page: currentPage,
        resultsPerPage: resultsPerPage,
        sortBy: sortBy,
        sortOrder: sortOrder,
        filters: this.currentFilters,
      });

      // Only render load more if we're not appending (to avoid conflicts with existing count)
      if (!options.appendToExisting) {
        this.renderLoadMore({
          currentPage: currentPage,
          resultsPerPage: resultsPerPage,
          totalResults: response.response?.total_num_results || results.length,
          collectionHandle: collectionHandle,
        });
      } else {
        // For append operations, check if we should hide load more after the products are rendered
        setTimeout(() => {
          this.renderLoadMore({
            currentPage: currentPage,
            resultsPerPage: resultsPerPage,
            totalResults: response.response?.total_num_results || results.length,
            collectionHandle: collectionHandle,
          });
        }, 100);
      }

      return true;
    } catch (error) {
      if (error.name === "NetworkError" || error.code === "ECONNABORTED") {
        const retryDelay = Math.min(1000 * Math.pow(2, this.retryCount), 10000);
        this.retryCount++;

        if (this.retryCount <= 3) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return this.handleBrowse(collectionHandle, options);
        }
      }

      this.retryCount = 0;

      this.hideLoadingState();
      this.showErrorState();

      return false;
    }
  }

  async renderProducts(handles, options = {}) {
    if (!handles || handles.length === 0) {
      this.showNoResults(options.collectionHandle);
      return;
    }

    try {
      const constructorProducts = options.constructorProducts || [];

      const hasConstructorData = constructorProducts.length > 0;
      let combinedProducts = [];

      if (!hasConstructorData) {
        this.showNoResults(options.collectionHandle);
        return;
      }

      combinedProducts = constructorProducts.map((constructorProduct) => {
        const item = constructorProduct.constructorData || {};
        const data = item.data || {};

        const productTitle = constructorProduct.value || item.value || data.title || "Product";

        return {
          handle: constructorProduct.handle,
          title: productTitle,
          price: Math.round((data.price || 0) * 100),
          compare_at_price: Math.round((data.compareAtPrice || 0) * 100),
          featured_image: {
            alt: productTitle,
            src: data.image_url || "",
          },
          images: data.mediaImages || [],
          available: (data.inventory_quantity || 0) > 0,
          vendor: data.vendor || "",
          product_type: data.product_type || "",
          options: data.options || [],
          variants:
            item.variations?.map((v) => {
              const variantTitle = v.data?.title || v.value || "Variant";

              let color = v.data?.color;
              if (!color && variantTitle) {
                const titleParts = variantTitle.split(/[\/,]/).map((s) => s.trim());
                color = titleParts[0];
              }

              return {
                id: v.data?.variation_id || v.id,
                title: variantTitle,
                price: Math.round((v.data?.price || data.price || 0) * 100),
                compare_at_price: Math.round((v.data?.compareAtPrice || data.compareAtPrice || 0) * 100),
                inventory_quantity: v.data?.inventory_quantity || 0,
                available: (v.data?.inventory_quantity || 0) > 0,
                image_url: v.data?.image_url,
                color: color,
                size: v.data?.size,
                discountPercentage: v.data?.discountPercentage || v.data?.fake_discount_percentage || 0,
              };
            }) || [],

          constructorData: constructorProduct.constructorData,
          variations: item.variations || constructorProduct.variations || [],
          matchedTerms: constructorProduct.matchedTerms || [],
          isSlotted: constructorProduct.isSlotted || false,
          url: this.transformUrlForMarket(data.url || `/products/${constructorProduct.handle}`),

          tags: [
            ...(data.group_ids || []),
            ...(data.tags ? data.tags.split(",").map((t) => t.trim()) : []),
            ...(data.preorder_tag ? ["preorder"] : []),
            ...(data.waterproof_tag ? ["waterproof"] : []),
            ...(data.bestseller_tag ? ["bestseller"] : []),
            ...(data.new_tag ? ["new"] : []),
            ...(data.low_inventory_tag ? ["low-inventory"] : []),
          ],

          isPreorder: data.preorder_tag || false,
          isWaterproof: data.waterproof_tag || false,
          isBestseller: data.bestseller_tag || false,
          isNew: data.new_tag || false,
          isLowInventory: data.low_inventory_tag || false,

          fakeDiscountPercentage: data.fake_discount_percentage || 0,
          fakeDiscountMessage: data.fake_discount_message || "",
        };
      });

      const productHTML = this.renderProductsTemplate(combinedProducts, options);

      const resultsContainer = this.getResultsContainer();

      if (!resultsContainer) {
        this.showErrorState();
        return;
      }

      this.hideDefaultShopifyResults();

      const existingGrid = resultsContainer.querySelector("[data-cnstrc-browse]");
      let finalShowingCount;

      if (existingGrid) {
        if (options.appendToExisting) {
          existingGrid.insertAdjacentHTML("beforeend", productHTML);
          const currentCount = parseInt(existingGrid.getAttribute("data-cnstrc-num-results")) || 0;
          const newTotalCount = currentCount + combinedProducts.length;
          existingGrid.setAttribute("data-cnstrc-num-results", newTotalCount);
          finalShowingCount = newTotalCount;
        } else {
          existingGrid.innerHTML = productHTML;
          existingGrid.setAttribute("data-cnstrc-num-results", combinedProducts.length);
          // For pagination, calculate based on page number and results per page
          const currentPage = options.currentPage || 1;
          const resultsPerPage = options.resultsPerPage || 40;
          const totalResults = options.totalResults || this.totalResultsFromAPI || combinedProducts.length;
          finalShowingCount = Math.min(currentPage * resultsPerPage, totalResults);
        }
        existingGrid.style.display = "";
        existingGrid.classList.add("constructor-loaded");
      } else {
        const gridHTML = `
          <ul class="product-list list-unstyled row mx-n3 mx-lg-n4 mb-0 constructor-loaded" 
              role="list"
              aria-label="Products in this collection"
              data-cnstrc-browse 
              data-cnstrc-collection-handle="${options.collectionHandle || ""}"
              data-cnstrc-num-results="${combinedProducts.length}"
              data-cnstrc-result-id="${options.resultId || ""}">
            ${productHTML}
          </ul>
        `;
        resultsContainer.innerHTML = gridHTML;
        finalShowingCount = combinedProducts.length;
      }

      // Update the results message with the correct count after grid is updated
      this.updateResultsMessage(options.collectionHandle, finalShowingCount);

      const finalGrid = resultsContainer.querySelector("[data-cnstrc-browse]");
      if (finalGrid && options.resultId) {
        finalGrid.setAttribute("data-cnstrc-result-id", options.resultId);
      }

      const productItems = resultsContainer.querySelectorAll(".product-item");

      const fallbackPagination = resultsContainer.querySelector(".constructor-fallback-pagination");
      if (fallbackPagination) {
        fallbackPagination.style.display = "none";
      }

      this.reinitializeComponents(resultsContainer);

      this.hideLoadingState();

      document.dispatchEvent(
        new CustomEvent("constructor:browse:results", {
          detail: {
            numResults: combinedProducts.length,
            collectionHandle: options.collectionHandle,
            appendToExisting: options.appendToExisting || false,
          },
        })
      );
    } catch (error) {
      this.hideLoadingState();
      this.showErrorState();
    }
  }

  renderProductsTemplate(products, options = {}) {
    if (!products || products.length === 0) {
      return '<li class="col-12"><p class="text-center">No products found.</p></li>';
    }

    const productHTML = products
      .map((product, index) => {
        return this.renderSingleProductTemplate(product, options);
      })
      .join("");

    return productHTML;
  }

  getCurrentMarket() {
    const fakeDiscountSettings = window.theme?.fakeDiscount;

    if (!fakeDiscountSettings) {
      return this.getFallbackMarket();
    }

    const detectedCountry = fakeDiscountSettings.currentMarket?.country;
    if (detectedCountry && fakeDiscountSettings.marketMapping) {
      const market = fakeDiscountSettings.marketMapping[detectedCountry];
      if (market) {
        return market;
      }
    }

    if (typeof window.Shopify !== "undefined" && window.Shopify.country) {
      const country = window.Shopify.country;
      const market = fakeDiscountSettings.marketMapping?.[country];
      if (market) {
        return market;
      }
    }

    const urlMarket = this.detectMarketFromUrl();
    if (urlMarket) {
      return urlMarket;
    }

    return "international";
  }

  getFallbackMarket() {
    if (typeof window.Shopify !== "undefined" && window.Shopify.country) {
      const mapping = {
        US: "us",
        CA: "canada",
        AU: "australia",
        GB: "uk",
        DE: "germany",
      };
      return mapping[window.Shopify.country] || "international";
    }
    return "international";
  }

  detectMarketFromUrl() {
    const hostname = window.location.hostname;
    const mappings = {
      ".ca": "canada",
      ".com.au": "australia",
      ".co.uk": "uk",
      ".de": "germany",
    };

    for (const [domain, market] of Object.entries(mappings)) {
      if (hostname.includes(domain)) return market;
    }
    return null;
  }

  /**
   * Transform Constructor.io URLs to match current market domain and locale
   * @param {string} constructorUrl - Original URL from Constructor.io
   * @returns {string} - Transformed URL for current market
   */
  transformUrlForMarket(constructorUrl) {
    if (!constructorUrl) return "";

    const currentHostname = window.location.hostname;
    const currentPathname = window.location.pathname;
    const currentMarket = this.getCurrentMarket();

    try {
      const url = new URL(constructorUrl);

      // Replace domain based on current hostname
      url.hostname = currentHostname;

      // Handle market-specific path prefixes
      if (currentMarket === "canada") {
        // Check if we're on French Canada site
        if (currentPathname.startsWith("/fr") || currentHostname.includes("fr.")) {
          // Add /fr prefix if not already present
          if (!url.pathname.startsWith("/fr")) {
            url.pathname = "/fr" + url.pathname;
          }
        }
        // For English Canada (.ca), no prefix needed
      } else if (currentMarket === "international" && currentHostname.includes(".com")) {
        // Keep as .com without prefix
      }

      return url.toString();
    } catch (error) {
      // If URL parsing fails, try simple string replacement
      console.warn("URL parsing failed, using fallback method:", error);

      let transformedUrl = constructorUrl;

      // Replace domain
      transformedUrl = transformedUrl.replace(/https?:\/\/[^\/]+/, `${window.location.protocol}//${currentHostname}`);

      // Add French prefix for French Canada
      if (currentMarket === "canada" && (currentPathname.startsWith("/fr") || currentHostname.includes("fr."))) {
        transformedUrl = transformedUrl.replace(/\/products\//, "/fr/products/");
      }

      return transformedUrl;
    }
  }

  /**
   * Get current market with enhanced detection
   * @returns {string} - Current market identifier
   */
  getCurrentMarketEnhanced() {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    // Enhanced Canada detection
    if (hostname.includes(".ca")) {
      return pathname.startsWith("/fr") || hostname.includes("fr.") ? "canada-fr" : "canada";
    }

    // Other markets
    if (hostname.includes(".com.au")) return "australia";
    if (hostname.includes(".co.uk")) return "uk";
    if (hostname.includes(".de")) return "germany";

    return this.getCurrentMarket(); // Fallback to existing method
  }

  bindSortEvents() {
    const bindEvents = () => {
      const sortItems = document.querySelectorAll("#constructor-sort-dropdown .dropdown-item");
      sortItems.forEach((item) => {
        item.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          const sortBy = item.getAttribute("data-value");
          const sortOrder = item.getAttribute("data-order");

          this.applySort(sortBy, sortOrder);
        });
      });

      document.addEventListener("click", (e) => {
        const sortWrapper = document.getElementById("constructor-sort-by-wrapper");
        const dropdown = document.getElementById("constructor-sort-dropdown");

        if (sortWrapper && !sortWrapper.contains(e.target)) {
          if (dropdown) {
            dropdown.style.display = "none";
          }
        }
      });
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", bindEvents);
    } else {
      bindEvents();
    }
  }

  getSmartFakeDiscount(product) {
    if (product.fakeDiscountPercentage && product.fakeDiscountPercentage > 0) {
      const basePrice = product.price || 0;
      const discountAmount = (basePrice * product.fakeDiscountPercentage) / 100;
      const discountedPrice = Math.ceil(basePrice - discountAmount);

      return {
        shouldApply: true,
        finalPrice: discountedPrice,
        originalPrice: product.compare_at_price || product.price || 0,
        message: product.fakeDiscountMessage || "",
        messageColor: window.theme?.fakeDiscount?.marketSettings?.messageColor || "#ff0000",
        discountPercentage: product.fakeDiscountPercentage,
        discountType: "percentage",
      };
    }

    const fakeDiscountSettings = window.theme?.fakeDiscount;

    if (!fakeDiscountSettings?.marketSettings?.enabled) {
      return {
        shouldApply: false,
        finalPrice: product.price || 0,
        originalPrice: product.compare_at_price || 0,
        message: null,
      };
    }

    const settings = fakeDiscountSettings.marketSettings;

    const hasDiscountAllowedTag =
      this.productHasTag(product, "discount allowed") || this.productHasTag(product, "discount-allowed");
    const hasConfiguredTag = this.productHasTag(product, settings.tag);
    const hasHighDiscount =
      product.compare_at_price &&
      product.price &&
      ((product.compare_at_price - product.price) / product.compare_at_price) * 100 >= 50;

    if (!hasDiscountAllowedTag && !hasConfiguredTag && !hasHighDiscount) {
      return {
        shouldApply: false,
        finalPrice: product.price || 0,
        originalPrice: product.compare_at_price || 0,
        message: null,
      };
    }

    let basePrice = 0;
    const priceSource = fakeDiscountSettings.priceSource || "price";

    if (priceSource === "price") {
      basePrice = product.price || 0;
    } else {
      if (product.compare_at_price && product.compare_at_price > product.price) {
        basePrice = product.compare_at_price;
      } else {
        basePrice = product.price || 0;
      }
    }

    let discountedPrice = 0;
    let actualPercentageForMessage = settings.percentage;

    if (settings.type === "fixed") {
      discountedPrice = parseFloat(settings.fixedAmount || 0) * 100;

      if (basePrice > 0) {
        actualPercentageForMessage = Math.round(((basePrice - discountedPrice) * 100) / basePrice);
      }
    } else {
      const discountAmount = (basePrice * settings.percentage) / 100;
      discountedPrice = basePrice - discountAmount;
      actualPercentageForMessage = settings.percentage;
    }

    let message = settings.message || "";

    message = message.replace(/\[percentage\]/g, settings.percentage);
    message = message.replace(/\[percentage-amount\]/g, actualPercentageForMessage);
    message = message.replace(/\[amount\]/g, settings.fixedAmount || "0");
    message = message.replace(/\[code\]/g, settings.code || "");

    const displayOriginalPrice = product.compare_at_price || product.price || 0;

    const roundedFinalPrice = Math.ceil(discountedPrice);

    return {
      shouldApply: true,
      finalPrice: roundedFinalPrice,
      originalPrice: displayOriginalPrice,
      message: message,
      messageColor: settings.messageColor,
      discountPercentage: actualPercentageForMessage,
      discountType: settings.type || "percentage",
      fixedAmount: settings.fixedAmount,
      basePrice: basePrice,
    };
  }

  productHasTag(product, requiredTag) {
    if (!requiredTag) return false;

    const tagMap = {
      preorder: product.isPreorder,
      "pre-order": product.isPreorder,
      waterproof: product.isWaterproof,
      bestseller: product.isBestseller,
      "best-seller": product.isBestseller,
      bestsellers: product.isBestseller,
      new: product.isNew,
      "low-inventory": product.isLowInventory,
    };

    if (tagMap[requiredTag] !== undefined) {
      return tagMap[requiredTag] === true;
    }

    if (product.tags && Array.isArray(product.tags)) {
      return product.tags.includes(requiredTag);
    }

    return false;
  }

  generateSwatchesFromConstructorData(swatchValues, uniqueColors, swatchSettings) {
    const maxVisible = swatchSettings.maxVisible || 4;

    let swatchesHTML = `
      <div class="swatches-container">
        <ul class="color-swatches list-unstyled mx-0 mb-5" role="list" aria-label="Color options">
    `;

    let visibleSwatchCount = 0;

    swatchValues.forEach((colorValue, index) => {
      const variationData = uniqueColors.get(colorValue);

      if (!variationData || visibleSwatchCount >= maxVisible) return;

      const isVisible = visibleSwatchCount < maxVisible;
      visibleSwatchCount++;

      swatchesHTML += `
        <li class="p-1" role="listitem" ${isVisible ? "" : "hidden"}>
          <button class="btn${(variationData.inventory_quantity || 0) > 0 ? "" : " swatch-unavailable"}" type="button"
                  data-img="${variationData.image_url || ""}"
                  data-img-width="600"
                  data-img-height="600" 
                  data-img-thumbnail="${window.theme?.productCard?.imgThumbnail || ""}"
                  data-variant-available="${(variationData.inventory_quantity || 0) > 0}"
                  data-color-value="${colorValue}"
                  data-action="change-swatch"
                  aria-label="Select ${colorValue} color option"
                  title="${colorValue}">
      `;

      if (swatchSettings.useVariantImages && variationData.image_url) {
        swatchesHTML += `
          <img class="img-fluid swatch-variant-image"
               src="${variationData.image_url}"
               alt="${colorValue}"
               width="40" height="40"
               loading="lazy" aria-hidden="true">
        `;
      } else {
        const swatchImageName = colorValue.toLowerCase().replace(/\s+/g, "-");
        const shopifySwatchUrl = `https://cdn.shopify.com/s/files/1/0262/9903/9821/files/swatch-${swatchImageName}.png?width=24&height=24&crop=center`;

        swatchesHTML += `
          <img class="img-fluid swatch-custom-image"
               src="${shopifySwatchUrl}"
               alt="${colorValue}"
               width="24" height="24"
               loading="lazy" aria-hidden="true"
               onerror="this.style.display='none'">
        `;
      }

      swatchesHTML += `
          </button>
        </li>
      `;
    });

    if (swatchValues.length > maxVisible) {
      const remainingCount = swatchValues.length - maxVisible;
      swatchesHTML += `
        <li class="color-swatch-more" role="listitem">
          <button type="button" 
                  class="btn btn-outline-secondary btn-sm rounded-circle d-flex align-items-center justify-content-center"
                  style="width: 32px; height: 32px; font-size: 10px;"
                  title="Show ${remainingCount} more colors"
                  aria-label="Show ${remainingCount} more color options"
                  data-action="show-all-swatches">
            +${remainingCount}
          </button>
        </li>
      `;
    }

    swatchesHTML += `
        </ul>
      </div>
    `;

    return {
      shouldShow: true,
      swatchesHTML: swatchesHTML,
      totalColors: swatchValues.length,
      visibleColors: Math.min(swatchValues.length, maxVisible),
    };
  }

  formatMoney(cents, options = {}) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    cents = Math.round(cents);

    if (typeof options === "string") {
      options = { format: options };
    }
    if (options.withCurrency) {
      const currencyCode = window.theme?.fakeDiscount?.currentMarket?.currency || "USD";

      try {
        const formatted = new Intl.NumberFormat(window.Shopify?.locale || "en-US", {
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);

        return formatted
          .replace(/\s?CA$/, "")
          .replace(/\s?US$/, "")
          .replace(/\s?USD$/, "")
          .replace(/\s?CAD$/, "");
      } catch (error) {}
    }

    const format = options.format || window.Shopify?.money_format || "${{amount}}";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    let value = "";

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ",";
      decimal = decimal || ".";

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split(".");
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsAmount = parts[1] ? decimal + parts[1] : "";

      return dollarsAmount + centsAmount;
    }

    switch (format.match(placeholderRegex)?.[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholderRegex, value);
  }

  formatMoneyWithCurrency(cents) {
    return this.formatMoney(cents, { withCurrency: true });
  }

  formatMoneySimple(cents) {
    return this.formatMoney(cents);
  }

  getSmartVariantSwatches(product) {
    const swatchSettings = window.theme?.variantSwatches;

    if (!swatchSettings?.enabled) {
      return { shouldShow: false, swatchesHTML: null };
    }

    const constructorData = product.constructorData?.data || product.data;
    const constructorVariations = product.constructorData?.variations || product.variations || constructorData?.variations;

    if (constructorVariations && Array.isArray(constructorVariations) && constructorVariations.length > 0) {
      const uniqueColors = new Map();

      constructorVariations.forEach((variation, index) => {
        let color = variation.data?.color || variation.color;

        if (!color && variation.data?.title) {
          const titleParts = variation.data.title.split(/[\/,]/).map((s) => s.trim());
          color = titleParts[0];
        }

        if (color && !uniqueColors.has(color)) {
          uniqueColors.set(color, {
            color: color,
            image_url: variation.data?.image_url || variation.image_url,
            price: variation.data?.price || variation.price,
            variation_id: variation.data?.variation_id || variation.variation_id,
            inventory_quantity: variation.data?.inventory_quantity || variation.inventory_quantity,
            title: variation.data?.title || variation.title,
          });
        }
      });

      if (uniqueColors.size > 0) {
        const swatchValues = Array.from(uniqueColors.keys());
        return this.generateSwatchesFromConstructorData(swatchValues, uniqueColors, swatchSettings);
      }
    }

    if (!product?.options || !product?.variants) {
      return { shouldShow: false, swatchesHTML: null };
    }

    let colorOption = null;
    let swatchValues = [];

    const colorOptionNames = swatchSettings.colorOptionNames
      ?.toLowerCase()
      .split(",")
      .map((name) => name.trim()) || ["color", "colour", "metal color", "metal colour"];

    for (const option of product.options) {
      const optionName = option.name?.toLowerCase() || "";

      if (colorOptionNames.some((name) => optionName.includes(name))) {
        colorOption = option;
        swatchValues = option.values || [];
        break;
      }
    }

    if (!colorOption || swatchValues.length <= 1) {
      return { shouldShow: false, swatchesHTML: null };
    }

    const maxVisible = swatchSettings.maxVisible || 4;

    let swatchesHTML = `
      <div class="swatches-container">
        <ul class="color-swatches list-unstyled mx-0 mb-5" role="list" aria-label="Color options">
    `;

    let visibleSwatchCount = 0;

    swatchValues.forEach((colorValue, index) => {
      let variantFound = null;
      const variants = product.variants || [];

      for (const variant of variants.slice().reverse()) {
        if (variant.title) {
          const variantTitle = variant.title.toLowerCase();
          const colorSearch = colorValue.toLowerCase();

          if (
            variantTitle.includes(colorSearch) ||
            colorSearch.includes(variantTitle.split(" ")[0]) ||
            colorSearch.includes(variantTitle.split("/")[0])
          ) {
            variantFound = variant;
            break;
          }
        }
      }

      const showSoldOut = swatchSettings.showSoldOut;
      const isVariantAvailable = variantFound ? variantFound.available : false;

      if (!showSoldOut && variantFound && !isVariantAvailable) {
        return;
      }

      const isHidden = visibleSwatchCount >= maxVisible ? "hidden" : "";
      visibleSwatchCount++;

      let variantImageUrl = product.featured_image?.src || product.image_url || "";

      if (variantFound?.featured_image?.src) {
        variantImageUrl = variantFound.featured_image.src;
      } else if (variantFound?.image?.src) {
        variantImageUrl = variantFound.image.src;
      }

      swatchesHTML += `
        <li class="p-1" role="listitem" ${isHidden}>
          <button class="btn${!isVariantAvailable ? " swatch-unavailable" : ""}" type="button"
                  data-img="${variantImageUrl}"
                  data-img-width="600"
                  data-img-height="600"
                  data-img-thumbnail="${window.theme?.productCard?.imgThumbnail || ""}"
                  data-variant-available="${isVariantAvailable}"
                  data-color-value="${colorValue}"
                  data-action="change-swatch"
                  aria-label="Select ${colorValue} color option"
                  title="${colorValue}">
      `;

      if (swatchSettings.useVariantImages && variantImageUrl) {
        swatchesHTML += `
          <img class="img-fluid swatch-variant-image"
               src="${variantImageUrl}"
               alt="${colorValue}"
               width="40" height="40"
               loading="lazy" aria-hidden="true">
        `;
      } else {
        const swatchImageName = colorValue.toLowerCase().replace(/\s+/g, "-");
        const shopifySwatchUrl = `https://cdn.shopify.com/s/files/1/0262/9903/9821/files/swatch-${swatchImageName}.png?width=24&height=24&crop=center`;

        swatchesHTML += `
          <img class="img-fluid swatch-custom-image"
               src="${shopifySwatchUrl}"
               alt="${colorValue}"
               width="24" height="24"
               loading="lazy" aria-hidden="true"
               onerror="this.style.display='none'">
        `;
      }

      swatchesHTML += `
          </button>
        </li>
      `;
    });

    const showMoreButton = visibleSwatchCount > maxVisible;
    if (showMoreButton) {
      const hiddenCount = visibleSwatchCount - maxVisible;
      swatchesHTML += `
        <li class="p-0" role="listitem">
          <button class="btn btn-view-more" type="button"
                  data-action="show-all-swatches"
                  aria-label="+${hiddenCount} more color options">
            +${hiddenCount}
          </button>
        </li>
      `;
    }

    swatchesHTML += `
        </ul>
      </div>
    `;

    return {
      shouldShow: visibleSwatchCount > 0,
      swatchesHTML: swatchesHTML,
      colorCount: visibleSwatchCount,
    };
  }

  getSmartPrimaryBadge(product, isOnSale, hasDiscount) {
    if (this.productHasTag(product, "preorder") || this.productHasTag(product, "pre-order")) {
      return { shouldShow: false, badgeHTML: null, badgeType: "preorder-skip" };
    }

    const promoBadgeSettings = window.theme?.promoBadge;
    if (promoBadgeSettings?.enabled && hasDiscount) {
      return this.getSmartPromoBadge(product);
    }

    const customBadgeSettings = window.theme?.customBadge;
    if (customBadgeSettings?.globalEnabled && customBadgeSettings?.marketSettings?.enabled) {
      return this.getSmartCustomBadge(product);
    }

    const commercialBadgeSettings = window.theme?.commercialBadge;
    if (commercialBadgeSettings?.enabled) {
      return this.getSmartCommercialBadge(product);
    }

    return { shouldShow: false, badgeHTML: null, badgeType: "none" };
  }

  getSmartCommercialBadge(product) {
    const commercialBadgeSettings = window.theme?.commercialBadge;

    if (!commercialBadgeSettings?.enabled) {
      return {
        shouldShow: false,
        badgeHTML: null,
        badgeType: "commercial-disabled",
      };
    }

    let badgeType = null;
    let badgeSettings = null;

    if (this.productHasTag(product, "waterproof")) {
      badgeType = "waterproof";
      badgeSettings = commercialBadgeSettings.waterproof;
    } else if (this.productHasTag(product, "new")) {
      badgeType = "new";
      badgeSettings = commercialBadgeSettings.new;
    } else if (this.productHasTag(product, "bestsellers")) {
      badgeType = "bestseller";
      badgeSettings = commercialBadgeSettings.bestseller;
    } else if (this.productHasTag(product, "low-inventory")) {
      badgeType = "lowInventory";
      badgeSettings = commercialBadgeSettings.lowInventory;
    }

    if (!badgeType || !badgeSettings?.text) {
      return {
        shouldShow: false,
        badgeHTML: null,
        badgeType: "commercial-no-match",
      };
    }

    const badgeHTML = `
      <div class="commercial-badge-wrapper position-absolute" style="z-index: 1;">
        <span class="commercial-badge ${badgeType}-badge"
              style="background-color: ${badgeSettings.bgColor}; 
                     color: ${badgeSettings.textColor}; 
                     font-size: ${commercialBadgeSettings.fontSize}px; 
                     border-radius: ${commercialBadgeSettings.borderRadius}px; 
                     opacity: ${commercialBadgeSettings.opacity / 100}; 
                     padding: ${commercialBadgeSettings.padding}px; 
                     width: ${commercialBadgeSettings.width}%; 
                     height: ${commercialBadgeSettings.height}%; 
                     display: inline-flex; 
                     align-items: center; 
                     justify-content: center;
                     font-weight: 500;
                     text-transform: uppercase;
                     line-height: 1.2;
                     margin: 10px;">
          ${badgeSettings.text}
        </span>
      </div>
    `;

    return {
      shouldShow: true,
      badgeHTML: badgeHTML,
      badgeType: `commercial-${badgeType}`,
    };
  }

  getSmartPromoBadge(product) {
    const promoBadgeSettings = window.theme?.promoBadge;

    if (!promoBadgeSettings?.enabled) {
      return {
        shouldShow: false,
        badgeHTML: null,
        badgeType: "promo-disabled",
      };
    }

    // Get first variant for price comparison (like Liquid)
    const variant = product.variants?.[0] || product;
    const compareAtPrice = variant.compare_at_price || product.compare_at_price || 0;
    const price = variant.price || product.price || 0;

    // Check if there's actually a discount
    if (!compareAtPrice || compareAtPrice <= price) {
      return { shouldShow: false, badgeHTML: null, badgeType: "no-discount" };
    }

    // Calculate savings percentage (replicate Liquid logic)
    const savingsPercentage = Math.round(((compareAtPrice - price) * 100) / compareAtPrice);

    // Round to nearest 5 (like Liquid logic)
    const remainder = savingsPercentage % 5;
    const roundedPercentage = remainder > 2 ? savingsPercentage + 5 - remainder : savingsPercentage - remainder;

    // Calculate opacity
    const opacity = (promoBadgeSettings.opacity || 100) / 100;
    const bgColor = promoBadgeSettings.bgColor || "#ff0000";

    // Extract RGB values for rgba
    const rgbMatch = bgColor.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    const r = rgbMatch ? parseInt(rgbMatch[1], 16) : 255;
    const g = rgbMatch ? parseInt(rgbMatch[2], 16) : 0;
    const b = rgbMatch ? parseInt(rgbMatch[3], 16) : 0;

    const badgeHTML = `
      <div class="promo-badge-wrapper position-absolute" style="z-index: 1;">
        <span class="promo-badge badge"
              style="padding: ${promoBadgeSettings.padding || 8}px;
                     width: ${promoBadgeSettings.width || 60}px;
                     height: ${promoBadgeSettings.height || 60}px;
                     font-size: ${promoBadgeSettings.fontSize || 12}px;
                     background-color: rgba(${r}, ${g}, ${b}, ${opacity});
                     color: ${promoBadgeSettings.textColor || "#ffffff"};
                     border-radius: ${promoBadgeSettings.borderRadius || 50}px;
                     display: inline-flex;
                     align-items: center;
                     justify-content: center;
                     font-weight: 500;
                     text-transform: uppercase;
                     line-height: 1.2;">
          ${roundedPercentage}% OFF
        </span>
      </div>
    `;

    return {
      shouldShow: true,
      badgeHTML: badgeHTML,
      badgeType: "promo",
      discountPercentage: roundedPercentage,
    };
  }

  getSmartCustomBadge(product) {
    const customBadgeSettings = window.theme?.customBadge;

    if (!customBadgeSettings?.globalEnabled || !customBadgeSettings?.marketSettings?.enabled) {
      return {
        shouldShow: false,
        badgeHTML: null,
      };
    }

    const settings = customBadgeSettings.marketSettings;

    const badgeHTML = `
      <div class="custom-badge-wrapper position-absolute" style="z-index: 1;">
        <span class="custom-badge badge"
              style="background-color: ${settings.bgColor}; 
                     color: ${settings.textColor}; 
                     font-size: ${settings.fontSize}px; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${settings.opacity / 100}; 
                     padding: ${settings.padding}px; 
                     width: ${settings.width}px; 
                     height: ${settings.height}px; 
                     display: flex; 
                     align-items: center; 
                     justify-content: center;">
          ${settings.text}
        </span>
      </div>
    `;

    return {
      shouldShow: true,
      badgeHTML: badgeHTML,
    };
  }

  getCurrentMarketData() {
    const fakeDiscountSettings = window.theme?.fakeDiscount;

    if (!fakeDiscountSettings?.currentMarket) {
      return {
        country: "US",
        currency: "USD",
        locale: "en-US",
      };
    }

    return {
      country: fakeDiscountSettings.currentMarket.country || "US",
      currency: fakeDiscountSettings.currentMarket.currency || "USD",
      locale: fakeDiscountSettings.currentMarket.locale || "en-US",
    };
  }

  renderSingleProductTemplate(product, options = {}) {
    if (!product) return "";

    const productTitle = product.title || "";
    const productHandle = product.handle || "";

    // Transform Constructor.io URL for current market
    let productUrl = product.url || product.constructorData?.url || `/products/${productHandle}`;
    if (product.constructorData?.url) {
      productUrl = this.transformUrlForMarket(product.constructorData.url);
    } else {
      productUrl = this.transformUrlForMarket(productUrl);
    }
    const firstVariant = (product.variants && product.variants[0]) || {};
    const isAvailable = product.available || false;

    const imgOrientation = window.theme?.productCard?.imgOrientation || "square";
    let imgWidth, imgHeight;

    switch (imgOrientation) {
      case "square":
        imgWidth = 600;
        imgHeight = 600;
        break;
      case "landscape":
        imgWidth = 600;
        imgHeight = 480;
        break;
      case "portrait":
        imgWidth = 480;
        imgHeight = 600;
        break;
      default:
        imgWidth = 600;
        imgHeight = 600;
    }

    const constructorData = product.constructorData?.data || product.constructorData || {};

    const mediaImages =
      Array.isArray(product.images) && product.images.length > 0
        ? product.images
        : Array.isArray(constructorData.mediaImages)
        ? constructorData.mediaImages
        : [];

    const primarySrcRaw =
      mediaImages[0]?.transformedSrc || mediaImages[0]?.src || product.featured_image?.src || constructorData.image_url || "";
    const secondarySrcRaw = mediaImages[1]?.transformedSrc || mediaImages[1]?.src || null;

    const primarySrc = primarySrcRaw ? this.buildSizedImageUrl(primarySrcRaw, imgWidth, imgHeight) : "";
    const secondarySrc = secondarySrcRaw ? this.buildSizedImageUrl(secondarySrcRaw, imgWidth, imgHeight) : null;
    const imageAlt = mediaImages[0]?.altText || product.featured_image?.alt || productTitle;

    const fakeDiscountResult = this.getSmartFakeDiscount(product);

    let finalPrice, originalPrice, isOnSale, discountPercentage, discountMessage;

    if (fakeDiscountResult.shouldApply) {
      finalPrice = fakeDiscountResult.finalPrice;
      originalPrice = fakeDiscountResult.originalPrice;
      isOnSale = true;
      discountPercentage = fakeDiscountResult.discountPercentage;
      discountMessage = fakeDiscountResult.message;
    } else {
      finalPrice = firstVariant.price || 0;
      originalPrice = firstVariant.compare_at_price || 0;
      isOnSale = originalPrice && originalPrice > finalPrice;
      discountPercentage =
        firstVariant.discountPercentage || (isOnSale ? Math.round(((originalPrice - finalPrice) / originalPrice) * 100) : 0);

      if (discountPercentage >= 50 && window.theme?.fakeDiscount?.marketSettings?.enabled) {
        const settings = window.theme.fakeDiscount.marketSettings;
        discountMessage = settings.message || "";
        discountMessage = discountMessage.replace(/\[percentage\]/g, discountPercentage);
        discountMessage = discountMessage.replace(/\[percentage-amount\]/g, discountPercentage);
      } else {
        discountMessage = null;
      }
    }

    const primaryBadgeResult = this.getSmartPrimaryBadge(product, isOnSale, discountPercentage > 0);
    const showSaleBadge = window.theme?.productCard?.showSaleBadge && isOnSale;
    const saleBadgeColor = window.theme?.productCard?.saleBadgeColor || "primary";

    const swatchesResult = this.getSmartVariantSwatches(product);

    const formattedPrice = this.formatMoneyWithCurrency(finalPrice);
    const formattedComparePrice = isOnSale && originalPrice ? this.formatMoneyWithCurrency(originalPrice) : "";

    const cardStyle = window.theme?.productCard?.style;
    const spacingMobile = window.theme?.productCard?.spacing?.mobile ? `p-${window.theme.productCard.spacing.mobile}` : "p-3";
    const spacingDesktop = window.theme?.productCard?.spacing?.desktop
      ? `p-lg-${window.theme.productCard.spacing.desktop}`
      : "p-lg-4";

    const imgThumbnail = window.theme?.productCard?.imgThumbnail || "";

    const marginClass = cardStyle !== "card" ? "mb-4" : "";

    return `
      <li class="product-item splide__slide ${spacingMobile} ${spacingDesktop} ${marginClass}" 
          role="listitem"
          data-product-handle="${productHandle}"
          data-cnstrc-item-id="${product.data?.id || product.id}"
          data-cnstrc-item-name="${this.sanitizeHTML(productTitle)}"
          data-cnstrc-item-variation-id="${firstVariant.id || ""}"
          data-cnstrc-item-price="${finalPrice / 100}">
        <div class="${cardStyle || ""}">
          <div class="img-wrapper position-relative mb-4${secondarySrc ? " has-hover-image" : ""}">
            <a href="${productUrl}">
              ${
                primaryBadgeResult.shouldShow
                  ? primaryBadgeResult.badgeHTML
                  : showSaleBadge && discountPercentage > 0
                  ? `<span class="img-badge-sale badge ${saleBadgeColor} text-white position-absolute" style="z-index: 1;">
                       ${window.theme?.product?.save || "Save"} ${discountPercentage}%
                     </span>`
                  : ""
              }
              ${
                primarySrc
                  ? `
                <img 
                  src="${primarySrc}"
                  alt="${imageAlt}"
                  class="product-item-img product-item-img--primary img-fluid rounded ${imgThumbnail} png-background"
                  width="${imgWidth}"
                  height="${imgHeight}"
                  loading="lazy"
                  decoding="async"
                />
                ${
                  secondarySrc
                    ? `
                <img 
                  src="${secondarySrc}"
                  alt="${imageAlt}"
                  class="product-item-img product-item-img--secondary img-fluid rounded ${imgThumbnail} png-background"
                  width="${imgWidth}"
                  height="${imgHeight}"
                  loading="eager"
                  decoding="async"
                />`
                    : ""
                }
              `
                  : `
                <div class="placeholder-image bg-light d-flex align-items-center justify-content-center" 
                     style="aspect-ratio: ${imgWidth}/${imgHeight}; min-height: 200px;">
                  <span class="text-muted">No Image</span>
                </div>
              `
              }
            </a>

            ${this.getSmartAddToCartForm(product)}
          </div>

            ${
              window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
                ? `
          <div class="wishlist-below-image mt-2 d-flex justify-content-end">
            <button class="btn-wishlist-add-remove btn" 
                    type="button"
                    data-product-handle="${productHandle}"
                    data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                    data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                    aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                    onclick="addOrRemoveFromWishlist(this)"
                    data-cnstrc-btn="add_to_wishlist">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" 
                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </button>
          </div>
            `
                : ""
            }
          
          <div class="firstline-container">
            <div class="${cardStyle === "card" ? "card-body" : ""}">
              <h3 class="product-item-title mb-2 ${window.theme?.productCard?.titleFontSizeMobile || "fs-6"} ${
      window.theme?.productCard?.titleFontSizeDesktop || "fs-lg-6"
    } ${window.theme?.productCard?.titleTruncate || ""}">
                <a href="${productUrl}" class="product-link position-relative d-block link-dark" tabindex="-1">
                  ${this.sanitizeHTML(productTitle)}
                </a>
              </h3>
              
              ${window.theme?.productCard?.showRating ? `<div class="product-rating-badge mb-2"></div>` : ""}
              
              <p class="product-item-price product-item-price-carrossel mb-2" data-product-item-price>
              ${
                isOnSale
                  ? `
                    <span class="product-item-price-compare text-muted me-1">
                      <span class="visually-hidden">${window.theme?.product?.priceRegular || "Regular price"}&nbsp;</span>
                      <s>${formattedComparePrice}</s>
                    </span>
                    <span class="product-item-price-final">
                      <span class="visually-hidden">${window.theme?.product?.priceSale || "Sale price"}&nbsp;</span>
                      ${formattedPrice}
                    </span>
                  `
                  : `
                    <span class="product-item-price-final">
                      ${formattedPrice}
                    </span>
                  `
              }
            </p>

              ${
                discountMessage
                  ? `
                <div class="fake-discount-message mt-2 p-2 rounded-2" 
                     style="color: ${
                       fakeDiscountResult.messageColor || window.theme?.fakeDiscount?.marketSettings?.messageColor || "#ff0000"
                     }; font-size: 0.875rem; line-height: 1.2;">
                  ${discountMessage}
                </div>
              `
                  : ""
              }

              ${swatchesResult.shouldShow ? swatchesResult.swatchesHTML : ""}
            </div>
          </div>
          
          ${this.getSmartAddToCartFormMobile(product)}
        </div>
      </li>
    `;
  }

  getResultsContainer() {
    let container = document.querySelector("#constructor-browse-results");
    if (container) {
      return container;
    }

    container = document.querySelector("[data-cnstrc-browse]");
    if (container && container.parentElement) {
      return container.parentElement;
    }

    container = document.querySelector("#collection .container");
    if (container) {
      return container;
    }

    return null;
  }

  debounce(func, wait) {
    let timeout;
    let lastCallTime = 0;

    const debounced = (...args) => {
      const now = Date.now();
      const timeSinceLastCall = now - lastCallTime;

      clearTimeout(timeout);

      if (timeSinceLastCall >= wait) {
        lastCallTime = now;
        return func.apply(this, args);
      }

      timeout = setTimeout(() => {
        lastCallTime = Date.now();
        func.apply(this, args);
      }, wait - timeSinceLastCall);
    };

    debounced.cancel = () => clearTimeout(timeout);

    return debounced;
  }

  throttle(func, limit) {
    let inThrottle;
    return function (...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  updateResultsMessage(collectionHandle, totalResults) {
    const messageElement = document.querySelector(".collection-results-message");
    if (messageElement) {
      const totalAvailable = this.totalResultsFromAPI || totalResults;

      const currentLocale = document.documentElement.lang || "en";
      const translations = {
        en: `Showing ${totalResults} of ${totalAvailable} products`,
        pt: `Exibindo ${totalResults} de ${totalAvailable} produtos`,
        es: `Mostrando ${totalResults} de ${totalAvailable} productos`,
        fr: `Affichage de ${totalResults} sur ${totalAvailable} produits`,
        de: `Es werden ${totalResults} von ${totalAvailable} Produkten angezeigt`,
      };

      const message = translations[currentLocale] || translations.en;
      messageElement.textContent = message;
      messageElement.classList.remove("constructor-hide-until-ready");

      // Ensure element is visible
      messageElement.style.display = "";

      // Update progress bar
      this.updateProgressBar(totalResults, totalAvailable);
    } else {
      console.warn("Constructor.io: collection-results-message element not found");
    }
  }

  updateProgressBar(currentResults, totalResults) {
    const progressBar = document.querySelector("#collection-pagination .progress-bar");
    if (progressBar && totalResults > 0) {
      const percentage = Math.round((currentResults / totalResults) * 100);
      progressBar.style.width = `${percentage}%`;
      progressBar.setAttribute("aria-valuenow", currentResults);
      progressBar.setAttribute("aria-valuemax", totalResults);
    }
  }

  showLoadingState() {
    const container = this.getResultsContainer();
    if (container) {
      container.classList.add("constructor-loading");

      const existingGrid = container.querySelector("[data-cnstrc-browse]");
      if (existingGrid) {
        existingGrid.classList.add("constructor-replacing");
      }

      let spinner = container.querySelector(".constructor-spinner");
      if (!spinner) {
        spinner = document.createElement("div");
        spinner.className = "constructor-spinner d-flex justify-content-center py-5";
        spinner.innerHTML =
          '<div class="spinner-border" role="status"><span class="visually-hidden">Loading search results...</span></div>';
        container.appendChild(spinner);
      }
    }
  }

  hideLoadingState() {
    const container = this.getResultsContainer();
    if (container) {
      container.classList.remove("constructor-loading");
      container.classList.remove("constructor-loading-subtle");

      const existingGrid = container.querySelector("[data-cnstrc-browse]");
      if (existingGrid) {
        existingGrid.classList.remove("constructor-replacing");
      }

      const spinner = container.querySelector(".constructor-spinner");
      if (spinner) {
        spinner.remove();
      }
    }
  }

  showErrorState() {
    const container = this.getResultsContainer();
    if (container) {
      const errorEl = document.createElement("div");
      errorEl.className = "alert alert-warning py-3";
      errorEl.setAttribute("role", "status");
      errorEl.textContent = "Search temporarily unavailable. Please try again.";

      const existingGrid = container.querySelector("[data-cnstrc-browse]");
      if (existingGrid) {
        existingGrid.classList.add("constructor-loaded");
        existingGrid.replaceWith(errorEl);
      }
    }

    this.hideLoadMore();
  }

  hideDefaultShopifyResults() {
    document.body.classList.add("constructor-browse-active");
    this.showConstructorUtilities();
  }

  showConstructorUtilities() {
    const utilities = document.getElementById("constructor-utilities");
    if (utilities) {
      utilities.style.display = "block";
    }
  }

  toggleSort() {
    const dropdown = document.getElementById("constructor-sort-dropdown");
    const trigger =
      document.querySelector("[data-action='toggle-sort']") ||
      document.querySelector("#constructor-sort-by-wrapper .sort-select");

    if (dropdown) {
      const isVisible = dropdown.style.display !== "none";
      dropdown.style.display = isVisible ? "none" : "block";

      if (trigger) {
        trigger.setAttribute("aria-expanded", (!isVisible).toString());
      }
    }
  }

  toggleFilters() {
    const offcanvas = document.getElementById("constructor-offcanvas-filters");

    if (offcanvas) {
      // Let Bootstrap handle the offcanvas via data attributes
      // Just load the filters when it's about to be shown
      this.loadConstructorFilters();

      // Bootstrap will handle the show/hide via data-bs-toggle="offcanvas"
      const bsOffcanvas = bootstrap.Offcanvas.getOrCreateInstance(offcanvas);
      bsOffcanvas.show();
    }
  }

  async loadConstructorFilters() {
    const filtersBody = document.getElementById("constructor-filters-body");

    if (!filtersBody) {
      return;
    }

    if (this.currentFacets && this.currentFacets.length > 0) {
      const filtersHTML = this.generateFiltersHTML(this.currentFacets);
      filtersBody.innerHTML = filtersHTML;
      this.bindFilterEvents();
    } else {
      const hardcodedHTML = this.generateHardcodedFilters();
      filtersBody.innerHTML = hardcodedHTML;
      this.bindFilterEvents();
    }
  }

  generateFiltersHTML(facets) {
    if (!facets || facets.length === 0) {
      return '<p class="text-muted">No filters available</p>';
    }

    // Separate price filters from other filters
    const priceFilters = facets.filter((f) => f.type === "range" && f.name === "price");
    const otherFilters = facets
      .filter((f) => {
        const opts = f.options || f.values || [];
        return f.name !== "price" && opts.length > 0 && opts.length <= 20;
      })
      .slice(0, 5);

    const allFilters = [...priceFilters, ...otherFilters];

    if (allFilters.length === 0) {
      return '<p class="text-muted">No filters available</p>';
    }

    return allFilters
      .map((facet, index) => {
        const isExpanded = index < 2;

        // Handle price range filters
        if (facet.type === "range" && facet.name === "price") {
          return this.generatePriceRangeFilter(facet, isExpanded);
        }

        // Handle other filters
        const options = (facet.options || facet.values || [])
          .map((opt) => {
            const isChecked = this.currentFilters[facet.name]?.includes(opt.value);
            return `
          <label class="d-flex align-items-center mb-2 cursor-pointer">
            <input type="checkbox" 
                   class="constructor-filter-checkbox me-2"
                   data-filter-name="${this.sanitizeHTML(facet.name)}"
                   data-filter-value="${this.sanitizeHTML(opt.value)}"
                   ${isChecked ? "checked" : ""}>
            <span class="small">${this.sanitizeHTML(opt.display_name || opt.value)} (${opt.count || 0})</span>
          </label>
        `;
          })
          .join("");

        return `
        <details class="filter-group mb-4" ${isExpanded ? "open" : ""}>
          <summary class="filter-toggle py-3 cursor-pointer list-none">
            <span class="h6 mb-0">${this.sanitizeHTML(facet.display_name || facet.name)}</span>
          </summary>
            <div class="filter-options pt-3">
            ${options}
            </div>
        </details>
      `;
      })
      .join("");
  }

  generatePriceRangeFilter(facet, isExpanded) {
    const facetMin = Math.ceil(facet.min);
    const facetMax = Math.floor(facet.max);

    // Store facet limits for validation
    this.priceLimits = { min: facetMin, max: facetMax };

    // Prefill with facet limits; user can overwrite
    let currentMin = this.currentFilters.price_min ?? facetMin;
    let currentMax = this.currentFilters.price_max ?? facetMax;

    // If a range is present, use it to prefill
    if (this.currentFilters.price && typeof this.currentFilters.price === "string" && this.currentFilters.price.includes("-")) {
      const parts = this.currentFilters.price.split("-");
      if (parts[0]) currentMin = parseInt(parts[0]);
      if (parts[1]) currentMax = parseInt(parts[1]);
    }

    // Get currency symbol
    const currencySymbol = window.Shopify?.currency?.symbol || "$";

    return `
      <details class="filter-group mb-4" ${isExpanded ? "open" : ""}>
        <summary class="filter-toggle py-3 cursor-pointer list-none">
          <span class="h6 mb-0">${this.sanitizeHTML(facet.display_name || facet.name)}</span>
        </summary>
        <div class="filter-options pt-3">
          <div class="price-range-inputs row mx-n2">
            <div class="col-6 px-2">
              <div class="input-group">
                <span class="input-group-text px-3">${currencySymbol}</span>
                <input
                  class="form-control constructor-price-input"
                  name="price_min"
                  value="${currentMin}"
                  type="number"
                  min="${facetMin}"
                  max="${facetMax}"
                  step="1"
                  placeholder="${facetMin}"
                  data-filter-name="price_min"
                  aria-label="Minimum price"
                >
              </div>
            </div>
            <div class="col-6 px-2">
              <div class="input-group">
                <span class="input-group-text px-3">${currencySymbol}</span>
                <input
                  class="form-control constructor-price-input"
                  name="price_max"
                  value="${currentMax}"
                  type="number"
                  min="${facetMin}"
                  max="${facetMax}"
                  step="1"
                  placeholder="${facetMax}"
                  data-filter-name="price_max"
                  aria-label="Maximum price"
                >
              </div>
            </div>
          </div>
          <div class="mt-3">
            <small class="text-muted">Range: ${currencySymbol}${facetMin} - ${currencySymbol}${facetMax}</small>
          </div>
        </div>
      </details>
    `;
  }

  generateHardcodedFilters() {
    const hardcodedFilters = [
      {
        name: "vendor",
        display_name: "Brand",
        options: [
          { value: "evry-jewels", display_name: "Evry Jewels", count: 0 },
          {
            value: "gold-collection",
            display_name: "Gold Collection",
            count: 0,
          },
          {
            value: "silver-collection",
            display_name: "Silver Collection",
            count: 0,
          },
        ],
      },
      {
        name: "product_type",
        display_name: "Product Type",
        options: [
          { value: "necklaces", display_name: "Necklaces", count: 0 },
          { value: "earrings", display_name: "Earrings", count: 0 },
          { value: "bracelets", display_name: "Bracelets", count: 0 },
          { value: "rings", display_name: "Rings", count: 0 },
        ],
      },
      {
        name: "tags",
        display_name: "Material",
        options: [
          { value: "gold", display_name: "Gold", count: 0 },
          { value: "silver", display_name: "Silver", count: 0 },
          { value: "rose-gold", display_name: "Rose Gold", count: 0 },
          {
            value: "sterling-silver",
            display_name: "Sterling Silver",
            count: 0,
          },
        ],
      },
    ];

    let html = '<div class="constructor-filters">';
    html += '<p class="text-muted small mb-3"><em>Basic filters (API integration in progress)</em></p>';

    hardcodedFilters.forEach((facet, index) => {
      const filterId = `constructor-filter-${index}`;
      const isExpanded = index < 2;

      html += `
        <div class="filter-group mb-4">
          <button
            class="filter-toggle w-100 d-flex justify-content-between align-items-center border-0 bg-transparent py-3"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#${filterId}"
            aria-expanded="${isExpanded}"
          >
            <span class="h6 mb-0">${facet.display_name}</span>
            <svg class="filter-arrow" width="12" height="12" viewBox="0 0 12 12">
              <path d="M2 4L6 8L10 4" stroke="currentColor" fill="none" stroke-width="1.5"/>
            </svg>
          </button>
          <div id="${filterId}" class="collapse ${isExpanded ? "show" : ""}">
            <div class="filter-options pt-3">
              ${this.generateFilterOptions(facet)}
            </div>
          </div>
        </div>
      `;
    });

    html += "</div>";
    return html;
  }

  generateFilterOptions(facet) {
    let html = '<ul class="filters-values-list list-unstyled mb-0">';

    facet.options.forEach((option) => {
      const optionId = `constructor-${facet.name}-${option.value}`.replace(/[^a-zA-Z0-9]/g, "-");
      const isChecked = this.currentFilters[facet.name]?.includes(option.value) || false;

      html += `
        <li class="form-check mb-2">
          <input
            type="checkbox"
            id="${optionId}"
            class="form-check-input mt-n1 me-3 constructor-filter-checkbox"
            data-filter-name="${facet.name}"
            data-filter-value="${option.value}"
            ${isChecked ? "checked" : ""}
          >
          <label class="form-check-label" for="${optionId}">
            <span class="small">${option.display_name || option.value} (${option.count || 0})</span>
          </label>
        </li>
      `;
    });

    html += "</ul>";
    return html;
  }

  bindFilterEvents() {
    const debouncedApplyFilters = this.debounce(() => {
      this.applyFilters();
    }, 300);

    const filtersBody = document.getElementById("constructor-filters-body");
    if (filtersBody) {
      // Remove existing handlers
      const existingChangeHandler = filtersBody.__constructorChangeHandler;
      const existingInputHandler = filtersBody.__constructorInputHandler;
      const existingBlurHandler = filtersBody.__constructorBlurHandler;
      const existingKeydownHandler = filtersBody.__constructorKeydownHandler;

      if (existingChangeHandler) {
        filtersBody.removeEventListener("change", existingChangeHandler);
      }
      if (existingInputHandler) {
        filtersBody.removeEventListener("input", existingInputHandler);
      }
      if (existingBlurHandler) {
        filtersBody.removeEventListener("blur", existingBlurHandler, true);
      }
      if (existingKeydownHandler) {
        filtersBody.removeEventListener("keydown", existingKeydownHandler);
      }

      // Handler for checkbox changes (apply immediately)
      const changeHandler = (e) => {
        if (e.target.classList.contains("constructor-filter-checkbox")) {
          this.updateCurrentFilters(e.target);
          debouncedApplyFilters();
        }
      };

      const inputHandler = (e) => {
        if (e.target.classList.contains("constructor-price-input")) {
          // On typing, update only the field being edited; no clamping/cross-sync yet
          this.updatePriceFilters(e.target, false);
          e.target.style.borderColor = "#ffc107";
          e.target.style.boxShadow = "0 0 0 0.2rem rgba(255, 193, 7, 0.25)";
        }
      };

      const blurHandler = (e) => {
        if (e.target.classList.contains("constructor-price-input")) {
          // If View Results is being clicked, skip apply here
          const isSuppressed = !!this.suppressBlurApply;

          // Normalize the blurred input
          this.updatePriceFilters(e.target, true);

          // Also capture the sibling input so both values are included
          const isMin = e.target.getAttribute("data-filter-name") === "price_min";
          const siblingSelector = isMin
            ? '.constructor-price-input[data-filter-name="price_max"]'
            : '.constructor-price-input[data-filter-name="price_min"]';
          const sibling = document.querySelector(siblingSelector);
          if (sibling) {
            this.updatePriceFilters(sibling, true);
          }

          e.target.style.borderColor = "";
          e.target.style.boxShadow = "";
          if (!isSuppressed) {
            debouncedApplyFilters();
          }
        }
      };

      const keydownHandler = (e) => {
        if (e.target.classList.contains("constructor-price-input") && e.key === "Enter") {
          e.target.blur(); // This will trigger the blur handler
        }
      };

      filtersBody.addEventListener("change", changeHandler);
      filtersBody.addEventListener("input", inputHandler);
      filtersBody.addEventListener("blur", blurHandler, true); // Use capture phase
      filtersBody.addEventListener("keydown", keydownHandler);

      filtersBody.__constructorChangeHandler = changeHandler;
      filtersBody.__constructorInputHandler = inputHandler;
      filtersBody.__constructorBlurHandler = blurHandler;
      filtersBody.__constructorKeydownHandler = keydownHandler;
    }
  }

  updatePriceFilters(input, normalize = false) {
    const filterName = input.getAttribute("data-filter-name");
    const inputValue = input.value.trim();
    let value = inputValue === "" ? null : parseInt(inputValue);

    // Clear any existing price range format
    delete this.currentFilters.price;

    // Apply validation against facet limits and sync counterpart (only when normalize=true)
    if (normalize && value !== null && !isNaN(value) && this.priceLimits) {
      const minLimit = this.priceLimits.min;
      const maxLimit = this.priceLimits.max;

      if (filterName === "price_min") {
        // Clamp to limits
        if (value < minLimit) value = minLimit;
        if (value > maxLimit) value = maxLimit;
        input.value = value;

        // If min goes above current max, set max = min
        const currentMax = this.currentFilters.price_max ?? maxLimit;
        if (currentMax !== null && !isNaN(currentMax) && value > currentMax) {
          this.currentFilters.price_max = value;
          const maxInput = document.querySelector('.constructor-price-input[data-filter-name="price_max"]');
          if (maxInput) maxInput.value = value;
        }
      } else if (filterName === "price_max") {
        // Clamp to limits
        if (value > maxLimit) value = maxLimit;
        if (value < minLimit) value = minLimit;
        input.value = value;

        // If max goes below current min, set min = max
        const currentMin = this.currentFilters.price_min ?? minLimit;
        if (currentMin !== null && !isNaN(currentMin) && value < currentMin) {
          this.currentFilters.price_min = value;
          const minInput = document.querySelector('.constructor-price-input[data-filter-name="price_min"]');
          if (minInput) minInput.value = value;
        }
      }
    }

    if (filterName === "price_min") {
      if (value === null || isNaN(value)) {
        // Field is empty - allow it
        delete this.currentFilters.price_min;
      } else if (value <= 0) {
        // Invalid value - delete filter, don't force input value
        delete this.currentFilters.price_min;
        input.value = ""; // Allow empty
      } else {
        this.currentFilters.price_min = value;
      }
    } else if (filterName === "price_max") {
      if (value === null || isNaN(value)) {
        // Field is empty - allow it
        delete this.currentFilters.price_max;
      } else if (value <= 0) {
        // Invalid value - delete filter, don't force input value
        delete this.currentFilters.price_max;
        input.value = ""; // Allow empty
      } else {
        this.currentFilters.price_max = value;
      }
    }
  }

  updateCurrentFilters(checkbox) {
    const filterName = checkbox.getAttribute("data-filter-name");
    const filterValue = checkbox.getAttribute("data-filter-value");

    if (!this.currentFilters[filterName]) {
      this.currentFilters[filterName] = [];
    }

    if (checkbox.checked) {
      if (!this.currentFilters[filterName].includes(filterValue)) {
        this.currentFilters[filterName].push(filterValue);
      }
    } else {
      this.currentFilters[filterName] = this.currentFilters[filterName].filter((value) => value !== filterValue);

      if (this.currentFilters[filterName].length === 0) {
        delete this.currentFilters[filterName];
      }
    }

    const keysToDelete = [];
    Object.keys(this.currentFilters).forEach((key) => {
      const value = this.currentFilters[key];
      if (!value || !Array.isArray(value) || value.length === 0) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => {
      delete this.currentFilters[key];
    });
  }

  applyFiltersWithPriceInputs() {
    const priceInputs = document.querySelectorAll(".constructor-price-input");

    // Normalize min first, then max, to avoid transient min>max states
    const minInput = document.querySelector('.constructor-price-input[data-filter-name="price_min"]');
    const maxInput = document.querySelector('.constructor-price-input[data-filter-name="price_max"]');

    if (minInput) this.updatePriceFilters(minInput, true);
    if (maxInput) this.updatePriceFilters(maxInput, true);

    priceInputs.forEach((input) => {
      input.style.borderColor = "";
      input.style.boxShadow = "";
    });

    this.applyFilters();
  }

  applyFilters() {
    const collectionHandle = this.currentCollectionHandle || window.cnstrc?.collectionHandle || "";

    if (!collectionHandle) {
      return;
    }

    const cleanFilters = {};
    Object.keys(this.currentFilters).forEach((key) => {
      const value = this.currentFilters[key];

      // Handle array filters (checkboxes)
      if (Array.isArray(value) && value.length > 0) {
        cleanFilters[key] = value;
      }
      // Handle price filters (min/max)
      else if ((key === "price_min" || key === "price_max") && value !== "" && value !== null && value !== undefined) {
        cleanFilters[key] = value;
      }
    });

    // Convert price_min and price_max to Constructor.io format (always use range)
    if (cleanFilters.price_min || cleanFilters.price_max) {
      // Create range format when we have any price values
      if (cleanFilters.price_min && cleanFilters.price_max) {
        // Both values provided
        cleanFilters.price = `${cleanFilters.price_min}-${cleanFilters.price_max}`;
      } else if (cleanFilters.price_min && !cleanFilters.price_max) {
        // Only min provided - use actual max limit
        const maxLimit = this.priceLimits ? this.priceLimits.max : 999999;
        cleanFilters.price = `${cleanFilters.price_min}-${maxLimit}`;
      } else if (!cleanFilters.price_min && cleanFilters.price_max) {
        // Only max provided - use actual min limit
        const minLimit = this.priceLimits ? this.priceLimits.min : 0;
        cleanFilters.price = `${minLimit}-${cleanFilters.price_max}`;
      }

      // Always remove individual price filters
      delete cleanFilters.price_min;
      delete cleanFilters.price_max;
    }

    this.currentFilters = cleanFilters;

    const urlParams = new URLSearchParams(window.location.search);
    const hasNoFilters = Object.keys(cleanFilters).length === 0;
    const currentResultsPerPage = parseInt(urlParams.get("resultsPerPage")) || 40;

    const resultsPerPageToUse = hasNoFilters ? 40 : currentResultsPerPage;

    this.handleBrowse(collectionHandle, {
      filters: cleanFilters,
      page: 1,
      resultsPerPage: resultsPerPageToUse,
    });
  }

  applySort(sortBy, sortOrder = "descending") {
    const currentSort = document.getElementById("constructor-current-sort");
    const sortItems = document.querySelectorAll("#constructor-sort-dropdown .dropdown-item");

    sortItems.forEach((item) => item.classList.remove("selected"));

    const selectedItem = document.querySelector(`[data-value="${sortBy}"][data-order="${sortOrder}"]`);
    if (selectedItem) {
      selectedItem.classList.add("selected");
      currentSort.textContent = selectedItem.textContent;
    }

    this.toggleSort();

    const urlParams = new URLSearchParams(window.location.search);
    const currentResultsPerPage = parseInt(urlParams.get("resultsPerPage")) || 40;

    this.handleBrowse(this.currentCollectionHandle, {
      sortBy: sortBy,
      sortOrder: sortOrder,
      page: 1,
      resultsPerPage: currentResultsPerPage,
    });
  }

  showNoResults(collectionHandle) {
    const container = this.getResultsContainer();
    if (container) {
      const noResultsEl = document.createElement("p");
      noResultsEl.className = "alert alert-warning py-3";
      noResultsEl.setAttribute("role", "status");
      noResultsEl.setAttribute("data-cnstrc-zero-result", "");
      noResultsEl.textContent = `No products found in collection "${collectionHandle}"`;

      const existingGrid = container.querySelector("[data-cnstrc-browse]");
      if (existingGrid) {
        existingGrid.classList.add("constructor-loaded");
        existingGrid.replaceWith(noResultsEl);
      }
    }

    this.hideLoadingState();
    this.hideLoadMore();
  }

  reinitializeComponents(container) {
    document.dispatchEvent(
      new CustomEvent("constructor:products-loaded", {
        detail: { container },
      })
    );

    if (window.ProductCard) {
      container.querySelectorAll(".product-item").forEach((card) => {
        new ProductCard(card);
      });
    }
  }

  updateURL(collectionHandle, params = {}) {
    const url = new URL(window.location);

    if (params.page && params.page > 1) {
      url.searchParams.set("page", params.page);
    } else {
      url.searchParams.delete("page");
    }

    if (params.resultsPerPage && params.resultsPerPage !== 40) {
      url.searchParams.set("resultsPerPage", params.resultsPerPage);
    } else {
      url.searchParams.delete("resultsPerPage");
    }

    if (params.sortBy && params.sortBy !== "relevance") {
      url.searchParams.set("sortBy", params.sortBy);
      url.searchParams.set("sort", `${params.sortBy}:${params.sortOrder || "descending"}`);
    } else {
      url.searchParams.delete("sortBy");
      url.searchParams.delete("sort");
    }

    if (params.sortOrder && params.sortOrder !== "descending") {
      url.searchParams.set("sortOrder", params.sortOrder);
    } else {
      url.searchParams.delete("sortOrder");
    }

    const hasActiveFilters =
      params.filters &&
      Object.keys(params.filters).some((key) => {
        const value = params.filters[key];
        return Array.isArray(value) && value.length > 0;
      });

    if (hasActiveFilters) {
      url.searchParams.set("filters", encodeURIComponent(JSON.stringify(params.filters)));
    } else {
      url.searchParams.delete("filters");
    }
    window.history.pushState(null, "", url.toString());
  }

  renderLoadMore(options) {
    const { currentPage, resultsPerPage, totalResults, collectionHandle } = options;

    // Check how many products are currently displayed
    const existingGrid = this.getResultsContainer()?.querySelector("[data-cnstrc-browse]");
    const currentlyDisplayed = existingGrid ? parseInt(existingGrid.getAttribute("data-cnstrc-num-results")) || 0 : 0;

    const hasMoreResults = currentlyDisplayed < totalResults;

    if (!hasMoreResults) {
      this.hideLoadMore();
      return;
    }

    let loadMoreContainer = document.querySelector(".constructor-load-more");
    if (!loadMoreContainer) {
      const resultsContainer = this.getResultsContainer();
      if (resultsContainer) {
        loadMoreContainer = document.createElement("div");
        loadMoreContainer.className = "constructor-load-more d-flex justify-content-center mt-4";
        resultsContainer.appendChild(loadMoreContainer);
      }
    }

    if (!loadMoreContainer) return;

    const loadMoreHTML = this.generateLoadMoreHTML(currentPage, collectionHandle, resultsPerPage);
    loadMoreContainer.innerHTML = loadMoreHTML;

    this.bindLoadMoreEvents(loadMoreContainer);

    loadMoreContainer.style.display = "flex";
  }

  generateLoadMoreHTML(currentPage, collectionHandle, resultsPerPage) {
    const nextPage = currentPage + 1;

    return `
      <button
        class="btn-load-more btn btn-outline-primary d-inline-flex align-items-center mt-5 mb-0 constructor-load-more-btn"
        data-next-page="${nextPage}"
        data-collection-handle="${collectionHandle}"
        data-per-page="${resultsPerPage}"
        data-action="load-more"
        aria-label="Load more products">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="me-2"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <path d="M7 13l5 5 5-5M7 6l5 5 5-5"/>
        </svg>
        Load More Products
      </button>
    `;
  }

  bindLoadMoreEvents(container) {
    const loadMoreBtn = container.querySelector(".constructor-load-more-btn");
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener("click", (e) => {
        e.preventDefault();
        const nextPage = parseInt(loadMoreBtn.dataset.nextPage);
        const collectionHandle = loadMoreBtn.dataset.collectionHandle;
        const perPage = parseInt(loadMoreBtn.dataset.perPage);

        this.loadMoreProducts(collectionHandle, nextPage, perPage);
      });
    }
  }

  hideLoadMore() {
    const loadMoreContainer = document.querySelector(".constructor-load-more");
    if (loadMoreContainer) {
      loadMoreContainer.style.display = "none";
    }
  }

  updateSortDropdown() {
    if (!this.currentSortOptions || this.currentSortOptions.length === 0) {
      return;
    }

    const sortDropdown = document.getElementById("sort-dropdown");
    const currentSortDisplay = document.getElementById("current-sort");

    if (!sortDropdown) {
      return;
    }

    // Clear existing options
    sortDropdown.innerHTML = "";

    // Add Constructor.io sort options
    this.currentSortOptions.forEach((option) => {
      const dropdownItem = document.createElement("div");
      dropdownItem.className = `dropdown-item ${option.status === "selected" ? "selected" : ""}`;
      dropdownItem.setAttribute("data-value", option.sort_by);
      dropdownItem.setAttribute("data-order", option.sort_order);
      dropdownItem.textContent = option.display_name;

      // Add click handler for Constructor.io sort
      dropdownItem.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Update visual state
        document.querySelectorAll("#sort-dropdown .dropdown-item").forEach((item) => item.classList.remove("selected"));
        dropdownItem.classList.add("selected");

        // Update current sort display
        if (currentSortDisplay) {
          currentSortDisplay.textContent = option.display_name;
        }

        // Close dropdown
        sortDropdown.style.display = "none";

        // Trigger Constructor.io search with new sort
        this.handleBrowse(this.currentCollectionHandle, {
          sortBy: option.sort_by,
          sortOrder: option.sort_order,
          filters: this.currentFilters,
        });
      });

      sortDropdown.appendChild(dropdownItem);
    });

    // Update current sort display
    const selectedOption = this.currentSortOptions.find((opt) => opt.status === "selected");
    if (selectedOption && currentSortDisplay) {
      currentSortDisplay.textContent = selectedOption.display_name;
    }
  }

  async loadMoreProducts(collectionHandle, nextPage, resultsPerPage) {
    const loadMoreBtn = document.querySelector(".constructor-load-more-btn");
    if (loadMoreBtn) {
      loadMoreBtn.disabled = true;
      loadMoreBtn.innerHTML = `
        <div class="spinner-border spinner-border-sm me-2" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        Loading...
      `;
    }

    const success = await this.handleBrowse(collectionHandle, {
      page: nextPage,
      resultsPerPage: resultsPerPage,
      appendToExisting: true,
    });

    if (!success && loadMoreBtn) {
      // Restore button if the request failed
      loadMoreBtn.disabled = false;
      loadMoreBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" class="me-2" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M7 13l5 5 5-5M7 6l5 5 5-5"/>
            </svg>
            Load More Products
          `;
    } else if (success && loadMoreBtn) {
      // If successful, the renderLoadMore will handle showing/hiding the button
      // Reset the button text for next use
      loadMoreBtn.disabled = false;
      loadMoreBtn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" class="me-2" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M7 13l5 5 5-5M7 6l5 5 5-5"/>
            </svg>
            Load More Products
          `;
    }

    return success;
  }

  bindEvents() {
    this.bindSortEvents();

    document.addEventListener("click", (e) => {
      const swatch = e.target.closest('[data-action="change-swatch"]');
      if (!swatch) return;

      e.preventDefault();
      e.stopPropagation();

      const productItem = swatch.closest(".product-item");
      if (!productItem) return;

      const allSwatches = productItem.querySelectorAll(".color-swatches .btn");
      allSwatches.forEach((s) => s.classList.remove("selected"));
      swatch.classList.add("selected");

      const img = productItem.querySelector(".product-item-img--primary");
      if (img) {
        const rawSrc = swatch.getAttribute("data-img");
        const width = parseInt(swatch.getAttribute("data-img-width")) || img.width || 600;
        const height = parseInt(swatch.getAttribute("data-img-height")) || img.height || 600;

        if (rawSrc) {
          const newSrc = this.buildSizedImageUrl(rawSrc, width, height);
          img.src = newSrc;
        }
      }
    });

    window.addEventListener("popstate", () => {
      const urlParams = new URLSearchParams(window.location.search);

      const page = parseInt(urlParams.get("page")) || 1;
      const resultsPerPage = parseInt(urlParams.get("resultsPerPage")) || 40;

      let sortBy = urlParams.get("sortBy");
      let sortOrder = urlParams.get("sortOrder");

      const legacySort = urlParams.get("sort");
      if (!sortBy && legacySort && legacySort.includes(":")) {
        const [sb, so] = legacySort.split(":");
        sortBy = sb || "relevance";
        sortOrder = so || "descending";
      }
      sortBy = sortBy || "relevance";
      sortOrder = sortOrder || "descending";

      const urlFilters = urlParams.get("filters");
      let filters = {};
      if (urlFilters) {
        try {
          filters = JSON.parse(decodeURIComponent(urlFilters));
        } catch (e) {
          console.warn("Invalid filters in URL, ignoring:", e);
        }
      }

      if (this.currentCollectionHandle) {
        this.handleBrowse(this.currentCollectionHandle, {
          page: page,
          resultsPerPage: resultsPerPage,
          sortBy: sortBy,
          sortOrder: sortOrder,
          filters: filters,
        });
      }
    });
  }

  handleFilterChange() {
    if (this.currentCollectionHandle) {
      this.handleBrowse(this.currentCollectionHandle, {
        filters: this.currentFilters,
      });
    }
  }

  getSmartAddToCartForm(product) {
    const settings = window.theme?.productCard;

    if (!settings?.showAtcForm) {
      return "";
    }

    const isCustomizedProduct =
      product.collections?.some((collection) => collection.handle === "customized") ||
      product.tags?.some((tag) => tag === "customized");

    if (isCustomizedProduct) {
      return this.generateViewProductButton(product);
    }

    if (!product || !product.variants || product.variants.length === 0) {
      return "";
    }

    const availableVariants = product.variants.filter((variant) => variant.available);

    if (!product.available || availableVariants.length === 0) {
      return this.generateSoldOutButton();
    }

    if (product.has_only_default_variant || availableVariants.length === 1) {
      return this.generateSimpleAddToCartButton(product, availableVariants[0]);
    }

    return this.generateDropdownAddToCartButton(product, availableVariants);
  }

  getSmartAddToCartFormMobile(product) {
    const settings = window.theme?.productCard;

    if (!settings?.showAtcForm) {
      return "";
    }

    const isCustomizedProduct =
      product.collections?.some((collection) => collection.handle === "customized") ||
      product.tags?.some((tag) => tag === "customized");

    if (isCustomizedProduct) {
      return this.generateViewProductButtonMobile(product);
    }

    if (!product || !product.variants || product.variants.length === 0) {
      return "";
    }

    const availableVariants = product.variants.filter((variant) => variant.available);

    if (!product.available || availableVariants.length === 0) {
      return this.generateSoldOutButtonMobile();
    }

    if (product.has_only_default_variant || availableVariants.length === 1) {
      return this.generateSimpleAddToCartButtonMobile(product, availableVariants[0]);
    }

    return this.generateDropdownAddToCartButtonMobile(product, availableVariants);
  }

  generateViewProductButton(product) {
    const translations = window.theme?.productCard?.translations;
    let productUrl = product.url || product.constructorData?.url || `/products/${product.handle}`;
    productUrl = this.transformUrlForMarket(productUrl);

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <a href="${productUrl}" 
             class="view-product-btn-desktop"
             data-action="view-product"
             role="button">
            ${translations?.viewProduct || "View Product"}
          </a>
        </div>
      </div>
    `;
  }

  generateSoldOutButton() {
    const translations = window.theme?.productCard?.translations;

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <button class="btn-atc btn w-100 btn-primary" data-action="sold-out" disabled>
            ${translations?.soldOut || "Sold Out"}
          </button>
        </div>
      </div>
    `;
  }

  generateSimpleAddToCartButton(product, variant) {
    const translations = window.theme?.productCard?.translations;
    let productUrl = product.url || `/products/${product.handle}`;
    productUrl = this.transformUrlForMarket(productUrl);

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${this.sanitizeHTML(product.title)}"
                data-product-url="${productUrl}">
            <input type="hidden" name="id" value="${variant.id}">
            <button class="btn-atc btn btn-md w-100 btn-primary"
                    type="submit"
                    data-action="add-to-cart-simple"
                    name="add"
                    data-cnstrc-btn="add_to_cart"
                    data-text-add-to-cart="${translations?.addToCart || "Add To Cart"}">
              ${translations?.addToCart || "Add To Cart"}
            </button>
          </form>
        </div>
      </div>
    `;
  }

  generateDropdownAddToCartButton(product, availableVariants) {
    const translations = window.theme?.productCard?.translations;
    const showInventory = window.theme?.productCard?.formShowInventory;
    let productUrl = product.url || `/products/${product.handle}`;
    productUrl = this.transformUrlForMarket(productUrl);

    const dropdownItems = availableVariants
      .map((variant) => this.generateVariantDropdownItem(variant, showInventory, translations))
      .join("");

    return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${this.sanitizeHTML(product.title)}"
                data-product-url="${productUrl}">
            <input type="hidden" name="id" value="${availableVariants[0].id}">
            <div class="w-200 dropup">
              <button type="button"
                      class="btn-atc btn btn-md dropdown-toggle btn-primary w-200"
                      data-action="add-to-cart-dropdown"
                      data-bs-toggle="dropdown"
                      data-text-add-to-cart="${translations?.addToCart || "Add To Cart"}"
                      aria-expanded="false"
                      aria-haspopup="true"
                      aria-label="${translations?.addToCart || "Add To Cart"} - Select variant">
                ${translations?.addToCart || "Add To Cart"}
              </button>
              <ul class="dropdown-menu shadow w-200">
                ${dropdownItems}
              </ul>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  generateVariantDropdownItem(variant, showInventory, translations) {
    const inventoryDisplay = this.getInventoryDisplay(variant, showInventory, translations);

    return `
      <li class="${variant.inventory_policy || ""}">
        <button class="dropdown-item"
                onclick="onClickAtcFormVariant(this, event)"
                data-variant-id="${variant.id}"
                type="button"
                data-cnstrc-btn="add_to_cart"
                aria-label="Select ${this.sanitizeHTML(variant.title)} variant"
                ${!variant.available ? "disabled aria-disabled='true'" : ""}>
          <div class="d-flex flex-column justify-content-start lh-sm">
            <span class="small">${this.sanitizeHTML(variant.title)}</span>
            ${inventoryDisplay}
          </div>
        </button>
      </li>
    `;
  }

  getInventoryDisplay(variant, showInventory, translations) {
    if (!showInventory) {
      return "";
    }

    if (variant.available) {
      if (!variant.inventory_quantity || variant.inventory_quantity <= 0 || variant.inventory_quantity >= 10) {
        return `<small class="text-success d-block">${translations?.inStock || "In stock"}</small>`;
      } else {
        const stockText = translations?.stockCount || "Only [count] left in stock";
        return `<small class="text-danger d-block">${stockText.replace("[count]", variant.inventory_quantity)}</small>`;
      }
    } else {
      return `<small class="text-muted d-block">${translations?.soldOut || "Sold out"}</small>`;
    }
  }

  generateViewProductButtonMobile(product) {
    const translations = window.theme?.productCard?.translations;
    let productUrl = product.url || product.constructorData?.url || `/products/${product.handle}`;
    productUrl = this.transformUrlForMarket(productUrl);

    return `
      <div class="mobile-view d-block d-lg-none">
        <a href="${productUrl}" 
           class="btn-vp w-100 d-block"
           data-action="view-product-mobile"
           role="button">
          ${translations?.viewProduct || "View Product"}
        </a>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  onclick="addOrRemoveFromWishlist(this)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
  }

  generateSoldOutButtonMobile() {
    const translations = window.theme?.productCard?.translations;

    return `
      <div class="mobile-view d-block d-lg-none">
        <button class="btn-atc btn w-100 btn-primary" disabled>
          ${translations?.soldOut || "Sold Out"}
        </button>
      </div>
    `;
  }

  generateSimpleAddToCartButtonMobile(product, variant) {
    const translations = window.theme?.productCard?.translations;
    let productUrl = product.url || `/products/${product.handle}`;
    productUrl = this.transformUrlForMarket(productUrl);

    return `
      <div class="mobile-view d-block d-lg-none">
        <form class="shopify-product-form product-item-form"
              onsubmit="onSubmitAtcForm(this, event)"
              data-product-id="${product.id}"
              data-product-title="${this.sanitizeHTML(product.title)}"
              data-product-url="${productUrl}">
          <input type="hidden" name="id" value="${variant.id}">
          <button class="btn-atc btn btn-md w-100 btn-primary"
                  type="submit"
                  name="add"
                  data-cnstrc-btn="add_to_cart"
                  data-text-add-to-cart="${translations?.addToCart || "Add to cart"}">
            ${translations?.addToCart || "Add to cart"}
          </button>
        </form>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  onclick="addOrRemoveFromWishlist(this)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
  }

  generateDropdownAddToCartButtonMobile(product, availableVariants) {
    const translations = window.theme?.productCard?.translations;
    const showInventory = window.theme?.productCard?.formShowInventory;
    let productUrl = product.url || `/products/${product.handle}`;
    productUrl = this.transformUrlForMarket(productUrl);

    const variantItems = availableVariants
      .map((variant) => this.generateVariantDropdownItemMobile(variant, showInventory, translations))
      .join("");

    return `
      <div class="mobile-view d-block d-lg-none">
        <form class="shopify-product-form product-item-form"
              onsubmit="onSubmitAtcForm(this, event)"
              data-product-id="${product.id}"
              data-product-title="${this.sanitizeHTML(product.title)}"
              data-product-url="${productUrl}">
          <input type="hidden" name="id" value="${availableVariants[0].id}">
          <div class="w-100 dropup">
            <button type="button"
                    class="btn-atc w-100"
                    data-action="add-to-cart-mobile"
                    data-bs-toggle="dropdown"
                    data-text-add-to-cart="${translations?.addToCart || "Add to cart"}"
                    aria-expanded="false"
                    aria-haspopup="true"
                    aria-label="${translations?.addToCart || "Add to cart"} - Select variant">
              ${translations?.addToCart || "Add to cart"}
            </button>
            <ul class="dropdown-menu shadow w-100">
              ${variantItems}
            </ul>
          </div>
        </form>
        
        ${
          window.theme?.wishlist?.enable && window.theme?.productCard?.showWishlistBtn
            ? `
          <button class="btn-wishlist-add-remove btn" 
                  type="button"
                  data-product-handle="${product.handle}"
                  data-text-add="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  data-text-remove="${window.theme?.wishlist?.removeText || "Remove from wishlist"}"
                  aria-label="${window.theme?.wishlist?.addText || "Add to wishlist"}"
                  onclick="addOrRemoveFromWishlist(this)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </button>
        `
            : ""
        }
      </div>
    `;
  }

  generateVariantDropdownItemMobile(variant, showInventory, translations) {
    const inventoryDisplay = this.getInventoryDisplay(variant, showInventory, translations);

    return `
      <li class="${variant.inventory_policy || ""}">
        <button class="dropdown-item"
                onclick="onClickAtcFormVariant(this, event)"
                data-variant-id="${variant.id}"
                type="button"
                data-cnstrc-btn="add_to_cart"
                aria-label="Select ${this.sanitizeHTML(variant.title)} variant"
                ${!variant.available ? "disabled aria-disabled='true'" : ""}>
          <div class="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center lh-sm">
            <span class="small">${this.sanitizeHTML(variant.title)}</span>
            ${inventoryDisplay}
          </div>
        </button>
      </li>
    `;
  }
}

window.onChangeConstructorProductSwatches = (btn, event) => {
  event.preventDefault();
  event.stopPropagation();

  const productItem = btn.closest(".product-item");
  if (!productItem) {
    return;
  }

  const allSwatchesInProduct = productItem.querySelectorAll(".color-swatches .btn");
  allSwatchesInProduct.forEach((swatch) => swatch.classList.remove("selected"));

  btn.classList.add("selected");

  const productImg = productItem.querySelector(".product-item-img");
  if (productImg && btn.dataset.img) {
    productImg.src = btn.dataset.img;

    if (btn.dataset.imgWidth) productImg.width = btn.dataset.imgWidth;
    if (btn.dataset.imgHeight) productImg.height = btn.dataset.imgHeight;
  }
};

window.showAllConstructorSwatchesInProduct = (btn, event) => {
  event.preventDefault();
  event.stopPropagation();

  const swatchesList = btn.closest(".color-swatches");
  if (!swatchesList) {
    return;
  }

  swatchesList.querySelectorAll("li[hidden]").forEach((li) => {
    li.removeAttribute("hidden");
  });

  btn.remove();
};

window.onConstructorLoadMore = (btn, event) => {
  event.preventDefault();
  event.stopPropagation();

  if (window.constructorBrowse && window.constructorBrowse.isInitialized) {
    const nextPage = parseInt(btn.dataset.nextPage);
    const collectionHandle = btn.dataset.collectionHandle;
    const perPage = parseInt(btn.dataset.perPage);

    window.constructorBrowse.loadMoreProducts(collectionHandle, nextPage, perPage);
  }
};

function initializeConstructorBrowse() {
  const conditions = {
    isCollectionPage: window.cnstrc?.isCollectionPage,
    browseEnabled: window.cnstrc?.browseEnabled,
    collectionElement: !!document.getElementById("collection"),
    cnstrcBrowseElement: !!document.querySelector("[data-cnstrc-browse]"),
  };

  const shouldInitialize =
    conditions.isCollectionPage || conditions.browseEnabled || conditions.collectionElement || conditions.cnstrcBrowseElement;

  if (shouldInitialize) {
    // Use singleton pattern to prevent multiple instances
    window.constructorBrowse = ConstructorBrowse.getInstance();
    window.constructorBrowse.hideDefaultShopifyResults();
  }
}

// Global initialization with error boundary
if (!window.constructorBrowseInitialized) {
  window.constructorBrowseInitialized = true;

  // Listen for Constructor.io client ready event
  document.addEventListener("constructor:client:ready", () => {
    initializeConstructorBrowse();
  });

  // Fallback initialization
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      setTimeout(initializeConstructorBrowse, 100);
    });
  } else {
    setTimeout(initializeConstructorBrowse, 100);
  }
}
